import { z } from 'zod';
import { queryProvenanceSchema } from './query-provenance.js';
import { CATALOGUE_ABSENCE_PATHS, CATALOGUE_AGGREGATIONS, CATALOGUE_ENTITIES, CATALOGUE_FIELD_PATHS, CATALOGUE_GRAIN_TIME, CATALOGUE_OPERATORS, CATALOGUE_REACH_KEYS, catalogueEntry, reachableFrom, } from './query-catalogue.js';
/**
 * The QueryDocument — what a declared query IS.
 *
 * ⚠️ **D16 — this is a PLATFORM primitive and the copilot is its first consumer,
 * not its owner.** Saved reports, scheduled reports and other surfaces may
 * consume it later, so nothing here assumes a copilot: no capability key, no
 * finding, no investigation, no wording. That is the difference between building
 * a private DSL and building a thing that can be exposed later without a
 * retrofit.
 *
 * ⚠️ **G1 — a model may FILL this; it may never author a query.** Every field
 * name in a document is a catalogue key that somebody declared. There is no
 * expression, no join clause, no column name and no SQL anywhere in the shape,
 * which is why `validateQuery` can be total: an invalid document is a refusal
 * with a reason, never a query that runs and answers something else.
 *
 * ⚠️ **G3 — there is NO TENANT FIELD, and there never may be.** A document
 * carries no tenant predicate because it could never be trusted to. The
 * repository adds it, as it does for every other read. Row-level security is not
 * active on this platform and the runtime role owns every table, so that
 * repository predicate is the only thing keeping tenants apart — a document that
 * could express its own scope would be a second, weaker mechanism beside it.
 * `query-document.test.ts` asserts the refusal directly rather than leaving it to
 * fall out of the catalogue lookup, because "it falls out" is how a property
 * stops being true.
 *
 * ⚠️ **G4 — a refusal is an ANSWER.** `validateQuery` returns a discriminated
 * result and never throws: every refusal names a code, the field it is about, and
 * a sentence a reader can act on. Measured on the live matcher, *"what is my
 * cheapest depot"* resolved to an idling report at 0.45 — a confident wrong
 * answer costs more trust than a refusal costs patience.
 *
 * **Nothing executes this yet.** The executor is the next piece, and the two
 * assertions of G14 that need it stay armed until it exists.
 */
/* ────────────────────────────── the shape ────────────────────────────────── */
export const queryFilterSchema = z
    .object({
    field: z.string().min(1),
    operator: z.enum(CATALOGUE_OPERATORS),
    /**
     * Absent for `isNull`, which is the whole predicate. A string or number for
     * `eq`/`gte`/`lte`/`contains`, a list for `in`, and a pair for `window`.
     */
    value: z.union([z.string(), z.number(), z.array(z.union([z.string(), z.number()]))]).optional(),
})
    .strict();
export const queryMeasureSchema = z
    .object({
    field: z.string().min(1),
    aggregation: z.enum(CATALOGUE_AGGREGATIONS),
})
    .strict();
/**
 * A comparison, declared rather than computed afterwards (G9).
 *
 * `PERIOD` is this window against the one before it; `BRANCH` is one branch of an
 * organisation against another. Both are refused for any measure whose merge rule
 * says it cannot be combined — the mean of medians is not the median, wherever
 * the two halves come from.
 */
export const queryCompareSchema = z.enum(['PERIOD', 'BRANCH']);
/**
 * How many dimensions one document may group by.
 *
 * ⚠️ **Named because the ROUTER now completes a dimension and has to know when there is no room.** It
 * was an inline `.max(3)`, so a copy of the number in `copilot.ts` would drift silently: raise the
 * schema and the completion would still refuse at three, so a document with three dimensions would
 * never receive the vehicle grouping it requires and would be refused for a reason the platform
 * itself created.
 */
export const MAX_QUERY_DIMENSIONS = 3;
/**
 * The row cap.
 *
 * ⚠️ **A cap is only safe when the ANSWER says it was applied.** A silently
 * truncated list reads as the whole picture — the fault `vehiclesTruncated`
 * exists to prevent on the vehicle picker, where "no match" meant *past the end
 * of the list* rather than *not reporting*. `validateQuery` returns `truncatedAt`
 * so the executor cannot forget to report it.
 */
export const QUERY_ROW_LIMIT = 500;
export const queryDocumentSchema = z
    .object({
    /** D20 — declared, never inferred, and a query may not mix two. */
    grain: z.enum(Object.keys(CATALOGUE_ENTITIES)),
    /** Catalogue keys to group by. Empty means one row for the whole scope. */
    dimensions: z.array(z.string().min(1)).max(MAX_QUERY_DIMENSIONS).default([]),
    measures: z.array(queryMeasureSchema).min(1).max(6),
    filters: z.array(queryFilterSchema).max(8).default([]),
    /** Hours back from the read's `asOf`. The window is one instant's question. */
    windowHours: z.number().int().positive().max(8_760).optional(),
    compare: queryCompareSchema.optional(),
    limit: z.number().int().positive().max(QUERY_ROW_LIMIT).optional(),
})
    .strict();
/* ─────────────────────────────── refusals ────────────────────────────────── */
/**
 * Every way a document can be refused.
 *
 * A CLOSED vocabulary, because each code is a different next action for the
 * reader: an undeclared field means *ask something else*, a refused aggregation
 * means *ask for it differently*, and an unconfigured dimension means *go and
 * configure your fleet*. Collapsing them into "invalid query" throws that away.
 */
export const QUERY_REFUSALS = [
    'UNDECLARED_FIELD',
    'OUT_OF_GRAIN',
    'MEASURE_OUT_OF_GRAIN',
    'NOT_GROUPABLE',
    'REFUSED_AGGREGATION',
    'UNDECLARED_AGGREGATION',
    'REFUSED_OPERATOR',
    'NOT_COMPARABLE',
    'READING_NEEDS_A_VEHICLE',
    'WINDOW_NOT_APPLICABLE',
    'WINDOW_REQUIRED',
    'TENANT_SCOPE_IS_NOT_EXPRESSIBLE',
    /**
     * ⚠️ Produced by the SERVICE, never by `validateQuery`. The validator is pure
     * and a document's shape does not change with an entitlement; this is the one
     * refusal that needs a database, and it shares the vocabulary so a client has
     * one shape to render rather than two.
     */
    'ENTITLEMENT_REQUIRED',
    'MALFORMED',
];
/* ────────────────────────────── validation ───────────────────────────────── */
const TIMELESS = ['vehicle', 'driver'];
/**
 * Validate a document against the catalogue. Total, and never throws.
 *
 * ⚠️ **Every refusal is collected, not the first one.** A caller told about one
 * problem at a time fixes it, resubmits, and is told about the next — which on a
 * model-filled document is a loop rather than a conversation.
 */
export function validateQuery(input) {
    const parsed = queryDocumentSchema.safeParse(input);
    if (!parsed.success) {
        return {
            ok: false,
            refusals: parsed.error.issues.map((i) => ({
                code: 'MALFORMED',
                field: i.path.join('.') || null,
                message: i.message,
            })),
        };
    }
    const doc = parsed.data;
    const grain = doc.grain;
    const refusals = [];
    const notes = [];
    const reach = reachableFrom(grain);
    /*
     * ⚠️ G3, asserted rather than assumed. `tenantId` is not a catalogue key, so
     * the lookup below would refuse it anyway as UNDECLARED_FIELD — and that is a
     * refusal for the wrong reason, which would quietly become the RIGHT reason if
     * anybody ever admitted a tenant field to the catalogue. Named explicitly, the
     * property survives that.
     */
    for (const f of [...doc.filters.map((x) => x.field), ...doc.dimensions]) {
        if (/(^|\.)tenant(Id)?$/i.test(f)) {
            refusals.push({
                code: 'TENANT_SCOPE_IS_NOT_EXPRESSIBLE',
                field: f,
                message: 'A query cannot name a tenant. Scope is applied by the repository on every read, and ' +
                    'a query that could express its own would be a second, weaker mechanism beside it.',
            });
        }
    }
    for (const key of doc.dimensions) {
        const entry = catalogueEntry(key);
        if (!entry) {
            refusals.push(undeclared(key));
            continue;
        }
        if (entry.kind !== 'DIMENSION') {
            refusals.push({
                code: 'NOT_GROUPABLE',
                field: key,
                message: `${entry.label} is a measure. It can be counted or averaged, not grouped by.`,
            });
            continue;
        }
        if (!reach.includes(entry.entity)) {
            refusals.push({
                code: 'OUT_OF_GRAIN',
                field: key,
                message: 
                /* No article: every grain label already begins with "one". */
                `${entry.label} describes ${CATALOGUE_ENTITIES[entry.entity].grain}, which a query ` +
                    `about ${CATALOGUE_ENTITIES[grain].grain} cannot reach.`,
            });
            continue;
        }
        if (!entry.groupable) {
            refusals.push({
                code: 'NOT_GROUPABLE',
                field: key,
                message: `${entry.label} cannot be grouped by.`,
            });
            continue;
        }
        /*
         * ⚠️ Grouping by identity is ALLOWED and produces a LIST — one row per
         * record, bounded only by the fleet. The first draft refused it on the
         * reasoning that a list is not a fold, which also refused "fuel level per
         * vehicle": the most natural fleet question there is. What is true is that
         * the cap decides what the reader sees, so the note says so.
         */
        if (entry.identity) {
            notes.push({
                kind: 'ROW_PER_RECORD',
                field: key,
                message: `One row per ${entry.entity}, ordered and capped — the answer says how many were ` +
                    `covered, because a truncated list read as the whole picture is the fault the cap ` +
                    `exists to avoid.`,
            });
        }
        if (entry.absence === 'UNCONFIGURED')
            noteUnconfigured(notes, entry);
    }
    for (const m of doc.measures) {
        const entry = catalogueEntry(m.field);
        if (!entry) {
            refusals.push(undeclared(m.field));
            continue;
        }
        if (entry.kind !== 'MEASURE') {
            refusals.push({
                code: 'UNDECLARED_AGGREGATION',
                field: m.field,
                message: `${entry.label} is something to group by, not something to measure.`,
            });
            continue;
        }
        /*
         * ⚠️ A measure must be AT the grain, where a dimension may be reached. One
         * incident has one vehicle, so grouping by its class counts nothing twice;
         * a vehicle has many trips, so summing trip distance at the incident grain
         * multiplies them against each other and returns more than the fleet drove.
         * Both are expressible and only one is a number.
         */
        if (entry.entity !== grain) {
            refusals.push({
                code: 'MEASURE_OUT_OF_GRAIN',
                field: m.field,
                message: `${entry.label} is measured per ${CATALOGUE_ENTITIES[entry.entity].grain}. Counting it ` +
                    `in a query about ${CATALOGUE_ENTITIES[grain].grain} would multiply the two together ` +
                    `and report more than happened. Ask for it at its own grain.`,
            });
            continue;
        }
        checkAggregation(refusals, entry, m.aggregation);
        checkComparability(refusals, notes, entry, doc.compare);
        if (entry.evidenceFloor) {
            notes.push({
                kind: 'EVIDENCE_FLOOR',
                field: entry.key,
                message: `${entry.label} is ${entry.evidenceFloor}.`,
            });
        }
    }
    checkReadings(refusals, notes, doc, grain);
    checkFilters(refusals, doc, reach);
    checkWindow(refusals, doc, grain);
    if (refusals.length > 0)
        return { ok: false, refusals };
    return {
        ok: true,
        document: doc,
        notes,
        truncatedAt: Math.min(doc.limit ?? QUERY_ROW_LIMIT, QUERY_ROW_LIMIT),
    };
}
function undeclared(key) {
    return {
        code: 'UNDECLARED_FIELD',
        field: key,
        message: `Nothing called "${key}" is declared, so no query can read it. A field has to be admitted ` +
            `to the catalogue with its meaning and its unit before it can be asked about.`,
    };
}
function noteUnconfigured(notes, entry) {
    if (notes.some((n) => n.field === entry.key))
        return;
    notes.push({
        kind: 'UNCONFIGURED',
        field: entry.key,
        message: `Vehicles with no ${entry.label} recorded are counted and named as unconfigured, never ` +
            `folded into a group that reads like one and never quietly dropped — excluding them would ` +
            `change the denominator of every figure beside them.`,
    });
}
function checkAggregation(refusals, entry, aggregation) {
    if (entry.refused.includes(aggregation)) {
        refusals.push({
            code: 'REFUSED_AGGREGATION',
            field: entry.key,
            message: `${entry.label} cannot be ${aggregation === 'sum' ? 'summed' : aggregation + 'ed'}. ` +
                `The type allows it and the meaning does not.`,
        });
        return;
    }
    if (!entry.aggregations.includes(aggregation)) {
        refusals.push({
            code: 'UNDECLARED_AGGREGATION',
            field: entry.key,
            message: `${entry.label} declares ${entry.aggregations.join(', ')}. ` +
                `"${aggregation}" is not among them.`,
        });
    }
}
function checkComparability(refusals, notes, entry, compare) {
    if (entry.merge !== 'NOT_AGGREGATABLE')
        return;
    const why = entry.mergeRefusal ?? 'it cannot be combined';
    if (compare === 'BRANCH') {
        refusals.push({
            code: 'NOT_COMPARABLE',
            field: entry.key,
            message: `${entry.label} cannot be compared across branches — ${why}.`,
        });
        return;
    }
    /*
     * Not a refusal without a branch comparison: the figure is perfectly good for
     * one branch, and the note is what stops a consolidated read presenting it as
     * the organisation's.
     */
    notes.push({
        kind: 'NOT_MERGEABLE_ACROSS_BRANCHES',
        field: entry.key,
        message: `${entry.label} is answered per branch — ${why}.`,
    });
}
/**
 * ⚠️ A reading belongs to ONE VEHICLE.
 *
 * A 2% van and a 90% tractor unit average to "the fleet is half full", which
 * describes no vehicle and no tank. So a `reading.*` measure has to be pinned to
 * a vehicle — grouped by one, or filtered to one — and a query that asks for the
 * fleet's fuel level as a single number is refused rather than answered.
 */
/**
 * The dimensions that pin a per-vehicle READING to ONE vehicle.
 *
 * ⚠️ **NOT "any vehicle dimension", and that distinction is the whole check.** `vehicle.status`,
 * `vehicle.class`, `vehicle.drivetrain` and `vehicle.base` are all vehicle dimensions and every one
 * of them groups MANY vehicles together — so a fuel level grouped by class is still the average of a
 * van's and a tractor unit's, which is exactly what `READING_NEEDS_A_VEHICLE` exists to refuse.
 *
 * ⚠️ **EXPORTED because the router now COMPLETES this requirement, and a second copy of the list
 * would be a second opinion on what "pinned" means.** The completion has to satisfy the same rule
 * this function enforces; two lists would drift and the failure is silent — a document completed with
 * a dimension the validator does not accept is refused for a reason the platform itself created.
 *
 * ⚠️ **`unique` IS THE PROPERTY, and it is declared rather than positional.** `registration` carries
 * a non-partial unique index per tenant (D3), so grouping a reading by it yields one row per
 * vehicle. `Vehicle.name` carries no unique index at all — it is free text a customer chose —
 * and measured on the development fleet **23 vehicles carry 13 distinct names**, so two lorries
 * sharing one name have their readings folded into a single row that describes neither.
 *
 * The document is still ASKABLE: on a fleet whose names happen to be unique it answers exactly what
 * was asked, and grouping by name is a reasonable thing to want. So it is a NOTE rather than a
 * refusal — `LABEL_NOT_UNIQUE` — and the reader is told which kind of answer they are holding.
 *
 * ⚠️ The completion in `validateRoutedCapability` picks the entry whose `unique` is true, never
 * `[0]`. It relied on ORDER until 28 September 2026, under a comment saying "the unique one is
 * first" — a property carried by position is one a later edit reorders without noticing, and here
 * the failure is silent: the platform would complete a document with a pin it knows to be ambiguous.
 */
export const VEHICLE_PINNING_DIMENSIONS = [
    { field: 'vehicle.registration', unique: true },
    { field: 'vehicle.name', unique: false },
];
/** The pinning dimension a completion may safely ADD. Absent if none is unique. */
export const uniqueVehiclePin = () => VEHICLE_PINNING_DIMENSIONS.find((d) => d.unique)?.field;
const pinFor = (field) => VEHICLE_PINNING_DIMENSIONS.find((d) => d.field === field);
function checkReadings(refusals, notes, doc, grain) {
    const readings = doc.measures
        .map((m) => catalogueEntry(m.field))
        .filter((e) => e?.kind === 'MEASURE' && !e.acrossVehicles);
    if (readings.length === 0)
        return;
    /*
     * ⚠️ A FILTER pins as well as a grouping, and it carries the same hazard: `vehicle.name = "Truck
     * 14"` selects every vehicle of that name, so a reading is summed across all of them.
     */
    const pins = [...doc.dimensions, ...doc.filters.map((f) => f.field)]
        .map((field) => pinFor(field))
        .filter((p) => p !== undefined);
    if (pins.length > 0) {
        /*
         * ⚠️ One unique pin is enough, and that is why this asks `some` rather than `every`. A document
         * grouping by registration AND name groups by the PAIR, which is unique — noting the name there
         * would be a caveat about an answer that does not have the problem.
         */
        if (!pins.some((p) => p.unique)) {
            for (const p of pins) {
                notes.push({
                    kind: 'LABEL_NOT_UNIQUE',
                    field: p.field,
                    message: `${catalogueEntry(p.field)?.label ?? p.field} is not unique — it is a name somebody ` +
                        `chose, and two vehicles may share one. Where they do, their readings are folded into ` +
                        `one row that describes neither. Group by registration for one row per vehicle.`,
                });
            }
        }
        return;
    }
    for (const r of readings) {
        refusals.push({
            code: 'READING_NEEDS_A_VEHICLE',
            field: r.key,
            message: `${r.label} is a reading from one vehicle, so it has no fleet-wide value — averaging a ` +
                `van's and a tractor unit's would describe neither. Name a vehicle, or ask for it per ` +
                `vehicle.`,
        });
    }
    void grain;
}
function checkFilters(refusals, doc, reach) {
    for (const f of doc.filters) {
        if (/(^|\.)tenant(Id)?$/i.test(f.field))
            continue; // already refused, once
        const entry = catalogueEntry(f.field);
        if (!entry) {
            refusals.push(undeclared(f.field));
            continue;
        }
        if (!reach.includes(entry.entity)) {
            refusals.push({
                code: 'OUT_OF_GRAIN',
                field: f.field,
                message: `${entry.label} cannot be reached from this query's grain.`,
            });
            continue;
        }
        const allowed = entry.kind === 'DIMENSION' ? entry.operators : ['gte', 'lte', 'eq'];
        if (!allowed.includes(f.operator)) {
            refusals.push({
                code: 'REFUSED_OPERATOR',
                field: f.field,
                message: `${entry.label} can be filtered by ${allowed.join(', ')} — not "${f.operator}".`,
            });
        }
        if (f.operator === 'in' && !Array.isArray(f.value)) {
            refusals.push({
                code: 'MALFORMED',
                field: f.field,
                message: '"in" needs a list of values.',
            });
        }
        if (f.operator !== 'isNull' && f.value === undefined) {
            refusals.push({
                code: 'MALFORMED',
                field: f.field,
                message: `"${f.operator}" needs a value.`,
            });
        }
    }
}
/**
 * ⚠️ A window is a question about a PERIOD, and two grains have none.
 *
 * A vehicle and a driver are what the fleet holds NOW; "vehicles in the last
 * seven days" either means every vehicle (so the window did nothing) or the ones
 * created in it (which is a different question nobody asked). Answering it
 * silently one way is a number that looks filtered and is not.
 */
function checkWindow(refusals, doc, grain) {
    const timeless = TIMELESS.includes(grain);
    if (timeless && doc.windowHours !== undefined) {
        refusals.push({
            code: 'WINDOW_NOT_APPLICABLE',
            field: 'windowHours',
            message: `A ${CATALOGUE_ENTITIES[grain].grain} is what the fleet holds now, not something that ` +
                `happened in a period. Ask about incidents or trips within a window instead.`,
        });
    }
    if (!timeless && doc.windowHours === undefined) {
        refusals.push({
            code: 'WINDOW_REQUIRED',
            field: 'windowHours',
            message: `A query about ${CATALOGUE_ENTITIES[grain].grain} needs a window. Without one the answer ` +
                `covers all of history, which is never what somebody means and grows quietly.`,
        });
    }
    if (doc.compare === 'PERIOD' && doc.windowHours === undefined) {
        refusals.push({
            code: 'WINDOW_REQUIRED',
            field: 'compare',
            message: 'Comparing periods needs a window to compare against the one before it.',
        });
    }
}
/**
 * Which grains a query may have, and what each excludes by construction.
 *
 * ⚠️ **The base predicate is part of the POPULATION the catalogue declares.**
 * "Vehicles" means vehicles that have not been archived; a query counting
 * archived ones would answer a different question under the same name.
 */
const GRAIN_DELEGATES = {
    vehicle: { delegate: 'vehicle', baseWhere: { deletedAt: null } },
    driver: { delegate: 'driver', baseWhere: { deletedAt: null } },
    incident: { delegate: 'operationalIncident', baseWhere: {} },
    trip: { delegate: 'trip', baseWhere: {} },
    recommendation: { delegate: 'recommendation', baseWhere: {} },
    event: { delegate: 'fleetEvent', baseWhere: {} },
    'vehicle-hour': { delegate: 'vehicleTelemetryHourly', baseWhere: {} },
};
/**
 * Entries with no column of their own, which the executor computes.
 *
 * ⚠️ Exported so the drift guard DERIVES this list rather than keeping a second
 * copy — a hand-written list in the test is a third copy of the vocabulary and
 * drifts with the thing it guards. The first version of the guard had exactly
 * that, and it went red on `driver.count` for the wrong reason.
 */
export const DERIVED_MEASURES = {
    'incident.count': 'COUNT',
    'trip.count': 'COUNT',
    'vehicle.count': 'COUNT',
    'event.count': 'COUNT',
    'driver.count': 'COUNT',
    'recommendation.count': 'COUNT',
    'incident.resolutionHours': 'RESOLUTION_HOURS',
};
/**
 * Where an entry sits relative to a grain: the relation hop, then its own path.
 *
 * ⚠️ **One resolution, used for the SELECT, the FILTER and the GROUPING.** A
 * dimension read one way for grouping and another for filtering is how a
 * filtered figure stops matching the group it claims to be part of, and neither
 * half looks wrong on its own.
 */
export function resolveFieldPath(grain, key) {
    const entry = catalogueEntry(key);
    if (!entry)
        return null;
    const within = CATALOGUE_FIELD_PATHS[key];
    if (!within)
        return null;
    if (entry.entity === grain)
        return within;
    const hop = CATALOGUE_REACH_KEYS[`${grain}→${entry.entity}`];
    return hop ? [hop.relation, ...within] : null;
}
/**
 * Resolve a VALIDATED document into a plan.
 *
 * ⚠️ **Only ever called with a document `validateQuery` accepted.** Every
 * judgement about whether a question is askable belongs to the validator; making
 * some of them twice is how two answers diverge, and the second one would be
 * made after the first was already stored.
 */
export function buildQueryPlan(document) {
    const grain = document.grain;
    const { delegate, baseWhere } = GRAIN_DELEGATES[grain];
    const include = {};
    const keys = [
        ...document.dimensions,
        ...document.measures.map((m) => m.field),
        ...document.filters.map((f) => f.field),
    ];
    for (const key of keys) {
        const entry = catalogueEntry(key);
        if (!entry)
            continue;
        const within = CATALOGUE_FIELD_PATHS[key];
        /*
         * ⚠️ **A relation WITHIN the grain's own entity still needs including**, and
         * missing this was a real bug rather than a theoretical one. `vehicle.class`
         * resolves to `['vehicleClass', 'name']`; at the VEHICLE grain there is no
         * hop, so the old code skipped it entirely, no relation was fetched, the
         * value read `undefined` and EVERY vehicle grouped as unconfigured.
         *
         * It answered correctly at the incident grain — where the hop include
         * happened to nest the relation — so "incidents by vehicle class" worked
         * while "vehicles by class" reported a fleet with no classes at all. Found
         * by driving the base dimension, which has the same shape; the case that
         * should have caught it asserted only that the unset count was above zero,
         * which is true either way.
         */
        if (entry.entity === grain) {
            if (within && within.length > 1)
                include[within[0]] = true;
            continue;
        }
        const hop = CATALOGUE_REACH_KEYS[`${grain}→${entry.entity}`];
        if (!hop)
            continue;
        /*
         * `vehicle.class` is one hop further, on the class the vehicle names.
         * Included because a declared field needs it — never by depth, which would
         * fetch relations nobody asked for.
         */
        const nested = within && within.length > 1 ? { include: { [within[0]]: true } } : true;
        const existing = include[hop.relation];
        include[hop.relation] = existing === true || existing === undefined ? nested : existing;
    }
    return {
        delegate,
        baseWhere,
        timeField: CATALOGUE_GRAIN_TIME[grain] ?? null,
        windowHours: document.windowHours ?? null,
        filters: document.filters.flatMap((f) => {
            const path = resolveFieldPath(grain, f.field);
            return path ? [{ path, operator: f.operator, value: f.value }] : [];
        }),
        dimensions: document.dimensions.flatMap((key) => {
            const path = resolveFieldPath(grain, key);
            const entry = catalogueEntry(key);
            if (!path)
                return [];
            const within = CATALOGUE_ABSENCE_PATHS[key];
            const hop = entry && entry.entity !== grain
                ? CATALOGUE_REACH_KEYS[`${grain}→${entry.entity}`]
                : undefined;
            return [
                {
                    key,
                    path,
                    reportsUnset: entry?.kind === 'DIMENSION' && !!entry.absence,
                    absencePath: within ? (hop ? [hop.relation, ...within] : within) : path,
                    ...(entry?.kind === 'DIMENSION' && entry.bucket ? { bucket: entry.bucket } : {}),
                },
            ];
        }),
        measures: document.measures.map((m) => {
            const entry = catalogueEntry(m.field);
            const parameter = entry?.kind === 'MEASURE' ? entry.parameter : undefined;
            if (parameter) {
                /*
                 * A reading reads a DIFFERENT COLUMN per aggregation — the rollup holds
                 * `_min`, `_max`, `_avg` and `_n` for each parameter — so the column is
                 * resolved here rather than sitting in the paths table, which has one
                 * entry per key.
                 */
                const hop = grain === 'vehicle-hour' ? [] : [];
                /*
                 * ⚠️ **`delta` IS THE ONE AGGREGATION THAT READS TWO COLUMNS**, and it
                 * is why `deltaMinPath` exists rather than a `${parameter}_delta` column
                 * that does not and should not.
                 *
                 * How far a cumulative counter moved over the window is
                 * `max(_max) - min(_min)` across the group — the highest reading anywhere
                 * in it, less the lowest. ⚠️ **NOT the sum of per-hour differences, and
                 * the two disagree by more than rounding:** measured on real data,
                 * 4,797.4 L against 4,499.0 L for one vehicle — about 6% — because a
                 * per-hour difference counts only what was burned BETWEEN the first and
                 * last sample INSIDE each hour and silently loses every inter-hour gap.
                 * The undercount is invisible and always in the flattering direction.
                 */
                if (m.aggregation === 'delta') {
                    return {
                        key: m.field,
                        aggregation: m.aggregation,
                        path: [...hop, `${parameter}_max`],
                        deltaMinPath: [...hop, `${parameter}_min`],
                        derived: null,
                        weightPath: null,
                    };
                }
                return {
                    key: m.field,
                    aggregation: m.aggregation,
                    path: [...hop, `${parameter}_${m.aggregation}`],
                    deltaMinPath: null,
                    derived: null,
                    weightPath: m.aggregation === 'avg' ? [...hop, `${parameter}_n`] : null,
                };
            }
            return {
                key: m.field,
                aggregation: m.aggregation,
                path: DERIVED_MEASURES[m.field] ? null : resolveFieldPath(grain, m.field),
                deltaMinPath: null,
                derived: DERIVED_MEASURES[m.field] ?? null,
                weightPath: null,
            };
        }),
        include,
        limit: document.limit ?? null,
    };
}
/* ──────────────────────────── entitlement ────────────────────────────────── */
/**
 * Which entitlements a document needs, read off the fields it names.
 *
 * ⚠️ **Separate from `validateQuery` because it is a different KIND of answer.**
 * Whether a question is well-formed is a fact about the question and is decided
 * with no database; whether this tenant may ask it is a fact about the tenant.
 * Folding the second into the first would make the validator impure and would
 * put an entitlement decision somewhere a saved document could be re-validated
 * without one.
 *
 * Returns the set, in declaration order, so a refusal can NAME what is missing
 * rather than saying "not entitled" — a reader who cannot tell which field cost
 * them the answer will remove the wrong one.
 */
export function requiredFeatures(document) {
    const keys = [
        ...document.dimensions,
        ...document.measures.map((m) => m.field),
        ...document.filters.map((f) => f.field),
    ];
    const out = [];
    for (const key of keys) {
        const entry = catalogueEntry(key);
        if (entry?.feature && !out.includes(entry.feature))
            out.push(entry.feature);
    }
    return out;
}
/** The fields that cost a given entitlement, so a refusal can name them. */
export function fieldsRequiring(document, feature) {
    const keys = [
        ...document.dimensions,
        ...document.measures.map((m) => m.field),
        ...document.filters.map((f) => f.field),
    ];
    return [...new Set(keys.filter((k) => catalogueEntry(k)?.feature === feature))];
}
/* ─────────────────────────────── the wire ────────────────────────────────── */
export const queryRefusalSchema = z
    .object({
    code: z.enum(QUERY_REFUSALS),
    field: z.string().nullable(),
    message: z.string(),
})
    .strict();
export const queryNoteSchema = z
    .object({
    kind: z.enum([
        'UNCONFIGURED',
        'NOT_MERGEABLE_ACROSS_BRANCHES',
        'EVIDENCE_FLOOR',
        'ROW_PER_RECORD',
        'LABEL_NOT_UNIQUE',
    ]),
    field: z.string(),
    message: z.string(),
})
    .strict();
export const queryResultRowSchema = z
    .object({
    dimensions: z.record(z.string().nullable()),
    measures: z.record(z.number().nullable()),
    records: z.number().int().nonnegative(),
})
    .strict();
export const queryResultSchema = z
    .object({
    rows: z.array(queryResultRowSchema),
    /**
     * Source records read — the denominator for everything above.
     *
     * ⚠️ Returned rather than kept internal, for the reason `samplesRead` is
     * everywhere else in this platform: an empty answer over 600 records and an
     * empty answer over none are opposite facts that draw identically.
     */
    scanned: z.number().int().nonnegative(),
    truncated: z.boolean(),
    rowsAvailable: z.number().int().nonnegative(),
    /** Per dimension, how many scanned records had no value for it. */
    unconfigured: z.record(z.number().int().nonnegative()),
    /** What the answer must SAY — decided at validation, not at render time. */
    notes: z.array(queryNoteSchema),
    /** The one instant the whole answer is true for. */
    asOf: z.string(),
    /**
     * G5's four layers — how the question was interpreted, what mechanism
     * produced the figures, which stored facts support them, and where the
     * underlying data arrived from.
     *
     * On the ANSWER rather than fetched separately: a figure and its provenance
     * that can be requested apart are a figure and its provenance that can be
     * shown apart.
     */
    provenance: queryProvenanceSchema,
})
    .strict();
/**
 * The request envelope.
 *
 * ⚠️ **Routing is NOT part of the document, and this is why the body is an
 * envelope rather than the document itself.** How a question was composed is a
 * fact about the moment it was asked; the document is the question. Folding
 * `routing` into the document would put it inside what D14 stores as "the
 * complete validated query", so a refinement chain would carry a claim about
 * interpretation into every later reading of it — and a saved report would store
 * how it was once typed.
 *
 * The route shipped taking a bare document one commit earlier; nothing consumed
 * it, and the envelope is the shape that survives step 4.
 */
export const queryRequestSchema = z
    .object({
    query: queryDocumentSchema,
    /**
     * DISCLOSURE, never authorization. It changes what the provenance says and
     * nothing about what may run, so a caller misreporting it gains nothing —
     * the same argument the copilot's own `routing` field records.
     */
    routing: z.enum(['DIRECT', 'MATCHED', 'MODEL']).default('DIRECT'),
    routedFrom: z.string().trim().min(1).max(400).nullable().default(null),
})
    .strict();
/* ─────────────────────── drilling into a figure ──────────────────────────── */
export const queryEvidenceRequestSchema = z
    .object({
    query: queryDocumentSchema,
    /**
     * The ROW being drilled into — one entry per declared dimension, exactly as
     * the answer returned it. `null` is the unset group.
     */
    row: z.record(z.string().nullable()),
    limit: z.number().int().positive().max(200).default(20),
})
    .strict();
/**
 * Turn a returned row back into predicates that select exactly its records.
 *
 * ⚠️ **This is NOT a second query.** The document is validated once, by
 * `validateQuery`, and the plan is built once — this only narrows the same read
 * to one group. Reconstructing a new document from the row would be a second
 * path through validation and entitlement, and two paths to the same data is
 * what this programme keeps refusing to build.
 *
 * ⚠️ **A dimension's TYPE decides how its value converts back**, and a timestamp
 * is the one that bites. The fold stringifies every group key — a `Date` becomes
 * an ISO string — so filtering `openedAt` by that string would compare a
 * timestamp column against text and match nothing, silently, returning "no
 * records" for a figure that plainly folded some. The catalogue declares the
 * type, so this is read rather than guessed.
 *
 * ⚠️ **`null` becomes IS NULL, never `= null`.** The unset group is a real group
 * — 126 of 132 vehicles have no class — and equality against null matches
 * nothing in SQL, so the one group a reader is most likely to drill into is the
 * one a naive conversion would empty.
 */
export function evidenceSelector(document, row) {
    const plan = buildQueryPlan(document);
    const out = [];
    for (const d of plan.dimensions) {
        const raw = row[d.key] ?? null;
        const entry = catalogueEntry(d.key);
        /*
         * ⚠️ **A BUCKETED DIMENSION CONTRIBUTES NO PREDICATE, and omitting this was a
         * 500 on both evidence paths.** A bucket's declared `type` is what the bucket
         * PRODUCES (`enum` for a weekday, `string` for a day) while its `path` targets
         * the DateTime column it REDUCES — so the conversion below built
         * `{ openedAt: 'Wed' }` and Prisma refused it: *"Invalid value for argument
         * `openedAt`: premature end of input. Expected ISO-8601 DateTime."*
         *
         * Found by DRIVING a composed answer — and the composer's own prompt tells a
         * model to group by these, so it is a document the model will produce. It hit
         * the copilot's evidence strip AND `/query/evidence`'s drill-through, because
         * both share this function.
         *
         * ⚠️ Skipped rather than translated. `DAY` would convert cleanly to a UTC day
         * range, but `HOUR_OF_DAY` and `WEEKDAY` are a DISJUNCTION whose size scales
         * with the window — thirteen Wednesdays over ninety days — so one uniform rule
         * beats three special cases, and a per-bucket range is a deliberate later
         * improvement. What must never happen is a predicate that silently matches
         * nothing or everything.
         *
         * ⚠️ Skipping alone would leave the evidence UNNARROWED and presented as the
         * group's rows, which is a claim the data does not support. `evidenceNarrowsFully`
         * is the other half, and callers decline rather than mislead.
         */
        if (entry?.kind === 'DIMENSION' && entry.bucket)
            continue;
        const isTimestamp = entry?.kind === 'DIMENSION' && entry.type === 'timestamp';
        /*
         * ⚠️ The ABSENCE path when the group is unset, the value path otherwise.
         * They differ only through a relation, and getting it wrong on this schema's
         * composite keys answers 0 or everything rather than failing.
         */
        out.push({
            path: raw === null ? d.absencePath : d.path,
            value: raw === null ? null : isTimestamp ? new Date(raw) : raw,
        });
    }
    return out;
}
/**
 * Whether evidence for a group can be narrowed to THAT group.
 *
 * ⚠️ **FALSE when any dimension is bucketed**, because a bucket reduces a column and
 * cannot be converted back into an equality on it (see `evidenceSelector`). Without
 * this a caller would fetch the window's leading rows and present them as the group's
 * — the rows would be real, the claim about them false, and nothing on screen able to
 * say so.
 *
 * Callers DECLINE rather than showing unnarrowed rows: the copilot omits its evidence
 * strip, and the drill-through refuses by name.
 */
export function evidenceNarrowsFully(document) {
    return document.dimensions.every((key) => {
        const entry = catalogueEntry(key);
        return !(entry?.kind === 'DIMENSION' && entry.bucket);
    });
}
//# sourceMappingURL=query-document.js.map